import React, { useCallback, useRef, useState, useEffect, useContext } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { RootStoreContext } from "../../../../../app/stores/rootStore";
import { useAIChat } from "../../../../../app/common/AIChat/AIChatContext";
import tools, { getToolById } from "../components/ToolRegistry";

// ── Default empty workflow ──
const defaultNodes = [
  {
    id: "input-1",
    type: "input",
    position: { x: 50, y: 200 },
    data: { label: "📥 化合物库", description: "输入分子集" },
  },
  {
    id: "output-1",
    type: "output",
    position: { x: 650, y: 200 },
    data: { label: "📤 筛选结果", description: "输出结果" },
  },
];

const WorkflowEditorInner = ({ projectId }) => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { screeningStore } = useContext(RootStoreContext);
  const { saveWorkflow, loadingWorkflow } = screeningStore;
  const { setActionHandler } = useAIChat();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [workflowName, setWorkflowName] = useState("未命名流程");
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // ── Register AI action handler ──
  useEffect(() => {
    console.log("[WorkflowEditor] Registering AI action handler");
    setActionHandler((action) => {
      console.log("[WorkflowEditor] AI action received:", action);
      if (action.action === "updateWorkflow" && action.data) {
        if (action.data.nodes) {
          // Convert nodes from AI format to React Flow format
          const flowNodes = action.data.nodes.map((n, i) => ({
            id: n.id || `ai-node-${i}`,
            type: "default",
            position: { x: n.positionX || n.position?.x || 0, y: n.positionY || n.position?.y || i * 120 },
            data: {
              label: `${n.icon || "🔧"} ${n.label || n.toolId || "Node"}`,
              toolId: n.toolId,
              config: n.config || {},
              status: "idle",
            },
          }));
          setNodes(flowNodes);
        }
        if (action.data.edges) {
          const flowEdges = action.data.edges.map((e, i) => ({
            id: e.id || `ai-edge-${i}`,
            source: e.sourceNodeId,
            target: e.targetNodeId,
            sourceHandle: e.sourcePort || null,
            targetHandle: e.targetPort || null,
            animated: true,
          }));
          setEdges(flowEdges);
        }
        setIsDirty(true);
      }
    });
    return () => setActionHandler(null);
  }, [setActionHandler, setNodes, setEdges]);

  // ── Canvas connection ──
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge({ ...params, animated: true }, eds));
      setIsDirty(true);
    },
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const toolData = event.dataTransfer.getData("application/reactflow");
      if (!toolData || !reactFlowInstance) return;

      const tool = JSON.parse(toolData);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: `${tool.id}-${Date.now()}`,
        type: "default",
        position,
        data: {
          label: `${tool.icon} ${tool.name}`,
          toolId: tool.id,
          config: {},
          description: tool.description,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsDirty(true);
    },
    [reactFlowInstance, setNodes]
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // ── Drag from palette start ──
  const onDragStart = (event, tool) => {
    event.dataTransfer.setData("application/reactflow", JSON.stringify(tool));
    event.dataTransfer.effectAllowed = "move";
  };

  // ── Run workflow (mock) ──
  const handleRun = async () => {
    setIsRunning(true);
    const toolNodes = nodes.filter((n) => n.data?.toolId);
    for (let i = 0; i < toolNodes.length; i++) {
      const nodeId = toolNodes[i].id;
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, status: "running" } } : n
        )
      );
      // Mock execution delay
      await new Promise((r) => setTimeout(r, 1000 + Math.random() * 2000));
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, status: Math.random() > 0.2 ? "success" : "failed" } }
            : n
        )
      );
    }
    setIsRunning(false);
    toast.current.show({ severity: "success", summary: "流程执行完成", life: 3000 });
  };

  // ── Update node config ──
  const updateNodeConfig = (key, value) => {
    if (!selectedNode) return;
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedNode.id
          ? { ...n, data: { ...n.data, config: { ...n.data.config, [key]: value } } }
          : n
      )
    );
    setIsDirty(true);
  };

  const handleSave = async () => {
    // Generate real UUIDs for nodes (React Flow uses string IDs)
    const uuid = () => "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
    const nodeIdMap = {};

    const nodesData = nodes.map((n, i) => {
      const realId = uuid();
      nodeIdMap[n.id] = realId;
      return {
        id: realId,
        nodeType: n.type === "input" ? "input" : n.type === "output" ? "output" : (n.data?.toolId ? "tool" : "default"),
        toolId: n.data?.toolId || null,
        label: n.data?.label || n.id,
        icon: n.data?.label?.match(/^.[^\s]*/) ? n.data.label.match(/^.[^\s]*/)[0] : null,
        configJson: n.data?.config ? JSON.stringify(n.data.config) : null,
        positionX: n.position.x,
        positionY: n.position.y,
        sortOrder: i,
      };
    });

    const edgesData = edges.map((e) => ({
      id: uuid(),
      sourceNodeId: nodeIdMap[e.source] || e.source,
      targetNodeId: nodeIdMap[e.target] || e.target,
      sourcePort: e.sourceHandle || null,
      targetPort: e.targetHandle || null,
    }));

    const success = await saveWorkflow({
      id: workflowId === "new" ? null : workflowId,
      projectId,
      name: workflowName,
      description: "",
      nodes: nodesData,
      edges: edgesData,
    });

    if (success) {
      setIsDirty(false);
      toast.current.show({ severity: "success", summary: "Workflow saved", life: 2000 });
    } else {
      toast.current.show({ severity: "error", summary: "Failed to save workflow", life: 3000 });
    }
  };

  return (
    <div className="screening-editor-layout">
      <Toast ref={toast} />

      {/* Toolbar - SectionHeading style */}
      <div className="screening-editor-toolbar">
        <div className="flex align-items-center gap-3">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-rounded"
            onClick={() => navigate("..")}
            tooltip="Back to Dashboard"
            style={{ color: "#fff" }}
          />
          <InputText
            value={workflowName}
            onChange={(e) => { setWorkflowName(e.target.value); setIsDirty(true); }}
            style={{ width: 240, fontSize: "0.9rem", fontWeight: 500, border: "none", background: "transparent", color: "#fff" }}
          />
        </div>
        <div className="flex align-items-center gap-2">
          {isDirty && <span style={{ fontSize: "0.75rem", color: "#fbbf24" }}>● unsaved</span>}
          <Button label="💾 Save" icon="pi pi-save" className="screening-header-btn" onClick={handleSave} />
          <Button label="▶️ Run" icon="pi pi-play" loading={isRunning}
            onClick={handleRun}
            className="screening-header-btn-primary" />
        </div>
      </div>

      {/* Editor Body */}
      <div className="screening-editor-body">
        {/* Canvas — full width, no left palette */}
        <div className="screening-editor-canvas" style={{ flex: 1 }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            fitView
            attributionPosition="bottom-left"
          >
            <Controls />
            <MiniMap
              nodeStrokeColor="#6366f1"
              nodeColor="#eef2ff"
              nodeBorderRadius={4}
              style={{ border: "1px solid #e2e8f0", borderRadius: 6 }}
            />
            <Background color="#e2e8f0" gap={20} />
          </ReactFlow>
        </div>

        {/* Config Panel */}
        {selectedNode && selectedNode.data?.toolId && (
          <div className="screening-editor-config">
            <div style={{ padding: 12, borderBottom: "1px solid #e2e8f0", fontWeight: 600, fontSize: "0.85rem", color: "#1e293b" }}>
              ⚙️ {selectedNode.data.label.replace(/^[^\s]+\s/, "")}
              <span style={{ float: "right", cursor: "pointer", color: "#94a3b8" }} onClick={() => setSelectedNode(null)}>✕</span>
            </div>
            <div style={{ padding: 12 }}>
              <ToolConfigForm toolId={selectedNode.data.toolId} config={selectedNode.data.config} onChange={updateNodeConfig} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tool Config Form ──
const ToolConfigForm = ({ toolId, config, onChange }) => {
  const tool = getToolById(toolId);
  if (!tool) return <div style={{ fontSize: "0.82rem", color: "#94a3b8" }}>Unknown tool</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {tool.parameters.map((param) => (
        <div key={param.key}>
          <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 500, color: "#374151", marginBottom: 4 }}>
            {param.label}
            {param.required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
          </label>
          {param.type === "number" && (
            <input
              type="number"
              defaultValue={config[param.key] ?? param.defaultValue}
              min={param.min}
              max={param.max}
              onChange={(e) => onChange(param.key, parseFloat(e.target.value))}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: "0.82rem" }}
            />
          )}
          {param.type === "range" && (
            <div>
              <input
                type="range"
                defaultValue={config[param.key] ?? param.defaultValue}
                min={param.min}
                max={param.max}
                step={param.step || 1}
                onChange={(e) => onChange(param.key, parseFloat(e.target.value))}
                style={{ width: "100%" }}
              />
              <div style={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
                {config[param.key] ?? param.defaultValue}
              </div>
            </div>
          )}
          {param.type === "select" && (
            <select
              defaultValue={config[param.key] ?? param.defaultValue}
              onChange={(e) => onChange(param.key, e.target.value)}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: "0.82rem", background: "#fff" }}
            >
              {param.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          {param.type === "string" && (
            <input
              type="text"
              defaultValue={config[param.key] ?? param.defaultValue}
              placeholder={param.placeholder}
              onChange={(e) => onChange(param.key, e.target.value)}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: "0.82rem" }}
            />
          )}
          {param.type === "file" && (
            <input
              type="file"
              accept={param.accept}
              onChange={(e) => onChange(param.key, e.target.files?.[0]?.name || "")}
              style={{ width: "100%", fontSize: "0.82rem" }}
            />
          )}
          {param.type === "boolean" && (
            <input
              type="checkbox"
              defaultChecked={config[param.key] ?? param.defaultValue}
              onChange={(e) => onChange(param.key, e.target.checked)}
            />
          )}
          {param.description && (
            <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 2 }}>{param.description}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Wrap with ReactFlowProvider ──
const WorkflowEditor = (props) => (
  <ReactFlowProvider>
    <WorkflowEditorInner {...props} />
  </ReactFlowProvider>
);

export default observer(WorkflowEditor);
